import React, { useEffect, useState } from "react";
import { Building2, Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { KAB_KOTA, PROVINSI, PROVINSI_DEFAULT, kecamatanOptionsOf, kelurahanOptionsOf } from "../lib/wilayah";
import { PhoneInput, isValidPhone, toPhoneE164, CoordinateInput, isValidCoordinate } from "../components/UI";
import LocationPicker from "../components/LocationPicker";

// Label & contoh isian dibedakan per role. `entitas` dan `lokasi` dipakai untuk
// menyusun judul seksi dan label field agar sesuai konteks masing-masing role.
const ROLE_CONFIG = {
  Pemda: {
    title: "Profil Pemda",
    subtitle: "Informasi instansi pemerintah daerah yang ditampilkan pada sistem KEDAN MBG.",
    kotaLabel: "Kabupaten/Kota",
    instansiPlaceholder: "Dinas Ketahanan Pangan Kota Medan",
    jenisPlaceholder: "Dinas / Badan / Sekretariat Daerah",
    wilayahLabel: "Wilayah Kerja",
    wilayahPlaceholder: "Kota Medan",
    entitas: "Instansi",
    lokasi: "Kantor",
    picLabel: "Nama PIC",
    testid: "profil-pemda-page",
  },
  Pemprov: {
    title: "Profil Pemprov",
    subtitle: "Informasi instansi pemerintah provinsi yang ditampilkan pada sistem KEDAN MBG.",
    kotaLabel: "Kota Kedudukan",
    instansiPlaceholder: "Dinas Ketahanan Pangan Provinsi Sumatera Utara",
    jenisPlaceholder: "Dinas / Badan / Biro",
    wilayahLabel: "Wilayah Kerja",
    wilayahPlaceholder: "Provinsi Sumatera Utara",
    entitas: "Instansi",
    lokasi: "Kantor",
    picLabel: "Nama PIC",
    testid: "profil-pemprov-page",
  },
  SPPG: {
    title: "Profil SPPG",
    subtitle: "Informasi Satuan Pelayanan Pemenuhan Gizi yang ditampilkan pada sistem KEDAN MBG.",
    kotaLabel: "Kabupaten/Kota",
    instansiPlaceholder: "SPPG Medan Petisah",
    jenisPlaceholder: "Dapur Umum / Satuan Pelayanan",
    wilayahLabel: "Wilayah Layanan",
    wilayahPlaceholder: "Kecamatan Medan Petisah",
    entitas: "SPPG",
    lokasi: "Dapur",
    picLabel: "Kepala SPPG",
    testid: "profil-sppg-page",
  },
};

// Jenis SPPG bersifat tetap; hanya berlaku untuk role SPPG.
const JENIS_SPPG = ["Mitra", "Pondok Pesantren", "TNI AD", "Polri"];

const EMPTY_FORM = {
  nama_instansi: "", jenis_instansi: "", nama_pic: "", jabatan_pic: "",
  no_telp: "", email_instansi: "", website: "", wilayah_kerja: "",
  provinsi: PROVINSI_DEFAULT, kota: "Medan", kecamatan: "", kelurahan: "", alamat: "",
  lat: 3.5952, lng: 98.6722,
};

export default function ProfilInstansiPage() {
  const { user } = useAuth();
  const cfg = ROLE_CONFIG[user?.role] || ROLE_CONFIG.Pemda;
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  // Profil SPPG: nama & email mengikuti akun terdaftar dan tidak dapat diubah di sini.
  const isSppg = user?.role === "SPPG";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agencies/me/profile");
        if (data) {
          setProfile(data);
          setForm({
            nama_instansi: isSppg ? (user?.name || "") : data.nama_instansi,
            jenis_instansi: data.jenis_instansi,
            nama_pic: data.nama_pic || "", jabatan_pic: data.jabatan_pic || "",
            no_telp: toPhoneE164(data.no_telp),
            email_instansi: isSppg ? (user?.email || "") : (data.email_instansi || ""),
            website: data.website || "", wilayah_kerja: data.wilayah_kerja || "",
            provinsi: data.provinsi || PROVINSI_DEFAULT,
            kota: data.kota, kecamatan: data.kecamatan || "", kelurahan: data.kelurahan || "",
            alamat: data.alamat, lat: data.lat, lng: data.lng,
          });
        } else if (isSppg) {
          setForm((f) => ({ ...f, nama_instansi: user?.name || "", email_instansi: user?.email || "" }));
        }
      } catch (e) {
        setMsg({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Data referensi kota/kecamatan/kelurahan hanya tersedia untuk Sumatera Utara.
  const isSumut = form.provinsi === PROVINSI_DEFAULT;

  const onProvinsiChange = (provinsi) => {
    if (provinsi === PROVINSI_DEFAULT) {
      const kota = KAB_KOTA[0][0];
      const defaultKec = kecamatanOptionsOf(kota)[0] || "";
      setForm((f) => ({
        ...f,
        provinsi,
        kota,
        kecamatan: defaultKec,
        kelurahan: kelurahanOptionsOf(kota, defaultKec)[0] || "",
        lat: KAB_KOTA[0][1],
        lng: KAB_KOTA[0][2],
      }));
      return;
    }
    setForm((f) => ({ ...f, provinsi, kota: "", kecamatan: "", kelurahan: "" }));
  };

  const onKotaChange = (kota) => {
    const c = KAB_KOTA.find((c) => c[0] === kota);
    const defaultKec = kecamatanOptionsOf(kota)[0] || "";
    setForm((f) => ({
      ...f,
      kota,
      kecamatan: defaultKec,
      kelurahan: kelurahanOptionsOf(kota, defaultKec)[0] || "",
      lat: c ? c[1] : f.lat,
      lng: c ? c[2] : f.lng,
    }));
  };

  const onKecamatanChange = (kec) => {
    setForm((f) => ({
      ...f,
      kecamatan: kec,
      kelurahan: kelurahanOptionsOf(f.kota, kec)[0] || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    if (!isValidPhone(form.no_telp)) {
      setMsg({ type: "error", text: "No. Telepon tidak valid. Masukkan minimal 8 angka setelah +62." });
      return;
    }
    if (!isValidCoordinate(form.lat, "lat") || !isValidCoordinate(form.lng, "lng")) {
      setMsg({ type: "error", text: "Koordinat tidak valid. Latitude -90 s/d 90, longitude -180 s/d 180." });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lat: Number(form.lat), lng: Number(form.lng) };
      if (isSppg) {
        payload.nama_instansi = user?.name || "";
        payload.email_instansi = user?.email || "";
      }
      if (profile) {
        const { data } = await api.patch(`/agencies/${profile.id}`, payload);
        setProfile(data);
      } else {
        const { data } = await api.post("/agencies", payload);
        setProfile(data);
      }
      setMsg({ type: "success", text: "Profil instansi berhasil disimpan." });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-3xl" data-testid="profil-instansi-loading">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid={cfg.testid}>
      <header>
        <div className="kedan-label text-slate-500">Akun {user?.role}</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">{cfg.title}</h1>
        <p className="text-slate-500 text-sm mt-1">{cfg.subtitle}</p>
      </header>

      <form onSubmit={submit} className="kedan-card p-6 space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-[#045498]" />
            <h2 className="font-display font-semibold text-slate-900">Data {cfg.entitas}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Nama {cfg.entitas}</label>
              <input
                className={`kedan-input ${isSppg ? "!bg-slate-100 !text-slate-800 cursor-not-allowed" : ""}`}
                required
                value={form.nama_instansi}
                onChange={(e) => set("nama_instansi", e.target.value)}
                readOnly={isSppg}
                disabled={isSppg}
                placeholder={cfg.instansiPlaceholder}
                data-testid="instansi-nama"
              />
              {isSppg && (
                <p className="text-xs text-slate-400 mt-1">
                  Mengikuti Nama Lengkap pada akun Anda. Ubah melalui menu Ubah Profil di kanan atas.
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Jenis {cfg.entitas}</label>
              {isSppg ? (
                <select
                  className="kedan-input"
                  required
                  value={form.jenis_instansi}
                  onChange={(e) => set("jenis_instansi", e.target.value)}
                  data-testid="instansi-jenis"
                >
                  <option value="">— Pilih jenis SPPG —</option>
                  {JENIS_SPPG.map((j) => <option key={j} value={j}>{j}</option>)}
                  {/* Nilai lama di luar daftar tetap ditampilkan agar tidak hilang diam-diam. */}
                  {form.jenis_instansi && !JENIS_SPPG.includes(form.jenis_instansi) && (
                    <option value={form.jenis_instansi}>{form.jenis_instansi} (data lama)</option>
                  )}
                </select>
              ) : (
                <input className="kedan-input" required value={form.jenis_instansi}
                  onChange={(e) => set("jenis_instansi", e.target.value)}
                  placeholder={cfg.jenisPlaceholder} data-testid="instansi-jenis" />
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">{cfg.wilayahLabel}</label>
              <input className="kedan-input" value={form.wilayah_kerja}
                onChange={(e) => set("wilayah_kerja", e.target.value)}
                placeholder={cfg.wilayahPlaceholder} data-testid="instansi-wilayah" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">No. Telepon</label>
              <PhoneInput required value={form.no_telp} onChange={(v) => set("no_telp", v)}
                placeholder="6145xxxxx" testid="instansi-telp" />
              <p className="text-xs text-slate-400 mt-1">
                Hanya angka. Nomor kantor ditulis tanpa 0 di depan kode area, mis. 081-4512345 → 814512345.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email {cfg.entitas}</label>
              <input
                type="email"
                className={`kedan-input ${isSppg ? "!bg-slate-100 !text-slate-800 cursor-not-allowed" : ""}`}
                value={form.email_instansi}
                readOnly={isSppg}
                disabled={isSppg}
                onChange={(e) => set("email_instansi", e.target.value)}
                placeholder="instansi@sumutprov.go.id" data-testid="instansi-email" />
              {isSppg && (
                <p className="text-xs text-slate-400 mt-1">
                  Email akun yang terdaftar. Hanya admin yang dapat mengubahnya.
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Website</label>
              <input className="kedan-input" value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="https://sumutprov.go.id" data-testid="instansi-website" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h2 className="font-display font-semibold text-slate-900 mb-4">Penanggung Jawab</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">{cfg.picLabel}</label>
              <input className="kedan-input" value={form.nama_pic}
                onChange={(e) => set("nama_pic", e.target.value)}
                placeholder="Person In Charge" data-testid="instansi-nama-pic" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Nama PIC</label>
              <input className="kedan-input" value={form.jabatan_pic}
                onChange={(e) => set("jabatan_pic", e.target.value)}
                placeholder="Nama PIC" data-testid="instansi-jabatan-pic" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h2 className="font-display font-semibold text-slate-900 mb-4">Alamat {cfg.lokasi}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Provinsi</label>
              <select className="kedan-input" required value={form.provinsi}
                onChange={(e) => onProvinsiChange(e.target.value)} data-testid="instansi-provinsi">
                {PROVINSI.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">{cfg.kotaLabel}</label>
              {isSumut ? (
                <select className="kedan-input" value={form.kota}
                  onChange={(e) => onKotaChange(e.target.value)} data-testid="instansi-kota">
                  {KAB_KOTA.map(([c]) => <option key={c}>{c}</option>)}
                </select>
              ) : (
                <input className="kedan-input" required value={form.kota}
                  onChange={(e) => set("kota", e.target.value)}
                  placeholder="Tulis kabupaten/kota" data-testid="instansi-kota" />
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Kecamatan</label>
              {isSumut ? (
                <select className="kedan-input" required value={form.kecamatan}
                  onChange={(e) => onKecamatanChange(e.target.value)} data-testid="instansi-kecamatan">
                  <option value="">Pilih Kecamatan</option>
                  {kecamatanOptionsOf(form.kota).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              ) : (
                <input className="kedan-input" required value={form.kecamatan}
                  onChange={(e) => set("kecamatan", e.target.value)}
                  placeholder="Tulis kecamatan" data-testid="instansi-kecamatan" />
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Kelurahan/Desa</label>
              {isSumut ? (
                <select className="kedan-input" required value={form.kelurahan}
                  onChange={(e) => set("kelurahan", e.target.value)} data-testid="instansi-kelurahan">
                  <option value="">Pilih Kelurahan/Desa</option>
                  {kelurahanOptionsOf(form.kota, form.kecamatan).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              ) : (
                <input className="kedan-input" required value={form.kelurahan}
                  onChange={(e) => set("kelurahan", e.target.value)}
                  placeholder="Tulis kelurahan/desa" data-testid="instansi-kelurahan" />
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Alamat Lengkap</label>
              <textarea rows="2" className="kedan-input" required value={form.alamat}
                onChange={(e) => set("alamat", e.target.value)} data-testid="instansi-alamat" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Titik Lokasi {cfg.lokasi}</label>
              <LocationPicker
                lat={form.lat}
                lng={form.lng}
                onChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
                restrictToSumut={isSumut}
                hint={`Klik pada peta atau geser penanda untuk menentukan titik lokasi ${cfg.lokasi.toLowerCase()}.`}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Latitude</label>
              <CoordinateInput axis="lat" value={form.lat} onChange={(v) => set("lat", v)} testid="instansi-lat" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Longitude</label>
              <CoordinateInput axis="lng" value={form.lng} onChange={(v) => set("lng", v)} testid="instansi-lng" />
            </div>
          </div>
        </div>

        {msg.text && (
          <div className={`text-sm rounded-md px-3 py-2 ${msg.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" :
            "bg-red-50 border border-red-200 text-red-700"
            }`} data-testid="instansi-msg">{msg.text}</div>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="kedan-btn-primary" data-testid="instansi-save">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Profil"}
          </button>
        </div>
      </form>
    </div>
  );
}
